import type { SectionSchema, SectionSchemaWithId } from "@repo/validators";
import type { z } from "zod";
import { useCallback } from "react";
import { Permissions } from "@repo/database";
import { capitalize } from "@repo/utils";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import type { Section } from "~/providers/section-provider";
import type { ManagerResult } from "~/types";
import { api } from "~/lib/api";
import { useSectionStore } from "~/stores/use-section-store";
import { usePermissionManager } from "./use-permission-manager";

export const useSectionManager = () => {
  const { t } = useTranslation("common");

  const { mutate: createSection } = api.section.createSection.useMutation();

  const { mutate: updateSection } = api.section.updateSection.useMutation({
    onSuccess() {
      toast.success(
        capitalize(t("update_item_success", { item: t("section") })),
      );
    },
  });

  const { setSelectedSection, setSectionDialogOpen } = useSectionStore();

  const { hasPermission } = usePermissionManager();

  const createSectionHandler = async (
    data: z.infer<ReturnType<typeof SectionSchema>>,
  ) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.create_section,
      label: t("Permissions.create_section_label"),
    });

    return new Promise<
      ManagerResult<keyof z.infer<ReturnType<typeof SectionSchema>>>
    >((resolve) => {
      if (!permission)
        return resolve({
          error: { path: "root", message: t("Permissions.no_permission") },
        });

      createSection(data, {
        onSuccess() {
          toast.success(t("create_item_success", { item: t("section") }));
          return resolve({
            data: true,
          });
        },
        onError() {
          return resolve({
            error: {
              path: "root",
              message: capitalize(
                t("create_item_error", { item: t("section") }),
              ),
            },
          });
        },
      });
    });
  };

  const updateSectionHandler = async (
    data: z.infer<typeof SectionSchemaWithId>,
  ) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.create_section,
      label: t("Permissions.update_section_label"),
    });

    return new Promise<
      ManagerResult<keyof z.infer<ReturnType<typeof SectionSchema>>>
    >((resolve) => {
      if (!permission)
        return resolve({
          error: { path: "root", message: t("Permissions.no_permission") },
        });

      updateSection(data, {
        onSuccess() {
          return resolve({
            data: true,
          });
        },
        onError() {
          resolve({
            error: {
              path: "root",
              message: capitalize(
                t("update_item_error", { item: t("section") }),
              ),
            },
          });
        },
      });
    });
  };
  const openSectionDialog = async (section?: Section) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.create_section,
      label: t("Permissions.create_section_label"),
    });

    if (!permission) return;

    setSelectedSection(section);
    setSectionDialogOpen(true);
  };
  return {
    createSection: createSectionHandler,
    updateSection: updateSectionHandler,
    openSectionDialog,
  };
};

export const useSectionUpdates = () => {
  const utils = api.useUtils();

  const handleSectionUpdate = useCallback(
    async (sectionId: string) => {
      const result = await utils.section.getById.fetch(sectionId);

      void utils.section.getAll.setData(undefined, (old) => {
        if (!old) return old;
        return [...old.filter((p) => p.id !== result.id), result];
      });
    },
    [utils.section.getAll, utils.section.getById],
  );

  const handleSectionDelete = useCallback(
    (sectionId: string) => {
      void utils.section.getAll.setData(undefined, (old) => {
        if (!old) return old;
        return old.filter((p) => p.id !== sectionId);
      });
    },
    [utils.section.getAll],
  );

  return {
    handleSectionUpdate,
    handleSectionDelete,
  };
};
